import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import ScheduleGrid from "../components/ScheduleGrid";
import Logo from "../components/Logo";
import CleaningChecklist from "../components/CleaningChecklist";
import MySLACard from "../components/MySLACard";
import ChangePasswordModal from "../components/ChangePasswordModal";
import AcceptTermsModal from "../components/AcceptTermsModal";
import LegalFooter from "../components/LegalFooter";
import { getCurrentPosition } from "../lib/geo";
import { toast } from "sonner";
import { LogOut, Play, Square, Calendar, Clock, History, BadgeCheck } from "lucide-react";
import logger from "../lib/logger";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EMPTY_WORKERS = {};

// Geolocation tuning ---------------------------------------------------------
// Rough meters-per-degree approximations used for cheap drift detection. These
// are good to ~1% accuracy for the Australian latitudes Avidity operates in.
const METERS_PER_DEG_LAT = 111000;
const METERS_PER_DEG_LNG = 88000;
const POSITION_DRIFT_METERS = 30;       // re-ping the server if user moved this far
const POSITION_HEARTBEAT_MS = 60_000;   // …or this often
const POSITION_MAX_AGE_MS = 30_000;
const POSITION_TIMEOUT_MS = 20_000;
const MS_PER_MINUTE = 60_000;
const HTTP_FORBIDDEN = 403;

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtDuration(min) {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

export default function WorkerDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("logbook"); // logbook | schedule | history
  const [sites, setSites] = useState([]);
  const [shifts, setShifts] = useState({});
  const [open, setOpen] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentShift, setCurrentShift] = useState(null); // {day, hour, current_site}

  const sitesById = useMemo(
    () => Object.fromEntries(sites.map((s) => [s.id, s])),
    [sites]
  );

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: shft }, { data: clk }, { data: now }] = await Promise.all([
        api.get("/sites"),
        api.get("/schedule/me"),
        api.get("/clock/me"),
        api.get("/schedule/me/now"),
      ]);
      setSites(s);
      const map = {};
      shft.forEach((x) => { map[`${x.day}|${x.hour}`] = x; });
      setShifts(map);
      setOpen(clk.open);
      setHistory(clk.events);
      setCurrentShift(now);
    } catch (err) {
      logger.error("Worker dashboard load failed:", err);
      toast.error("Could not load your dashboard");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Background geofence monitor — pings the server with current position
  // every ~60s (or sooner if the user moves >30m) while the worker is clocked
  // in. The backend stamps the event with `left_geofence_at` if they leave.
  useEffect(() => {
    if (!open || !navigator.geolocation) return undefined;
    let lastSent = 0;
    let lastLat = null;
    let lastLng = null;
    const send = async (lat, lng) => {
      try {
        const { data } = await api.post("/clock/position", { latitude: lat, longitude: lng });
        if (data?.left_geofence_at) {
          toast.error("You have left the site geofence — admin notified.");
        }
      } catch (err) {
        logger.warn("position ping failed:", err?.response?.data?.detail || err.message);
      }
    };
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const now = Date.now();
        const movedFar = lastLat != null
          ? Math.hypot((latitude - lastLat) * METERS_PER_DEG_LAT, (longitude - lastLng) * METERS_PER_DEG_LNG) > POSITION_DRIFT_METERS
          : true;
        if (now - lastSent > POSITION_HEARTBEAT_MS || movedFar) {
          lastSent = now;
          lastLat = latitude;
          lastLng = longitude;
          send(latitude, longitude);
        }
      },
      (err) => logger.warn("watchPosition error:", err.message),
      { enableHighAccuracy: true, maximumAge: POSITION_MAX_AGE_MS, timeout: POSITION_TIMEOUT_MS }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [open]);

  const clockIn = useCallback(async (siteId) => {
    let coords = null;
    try {
      const pos = await getCurrentPosition();
      coords = { latitude: pos.latitude, longitude: pos.longitude };
    } catch (err) {
      logger.warn("Geolocation unavailable:", err.message);
      toast.error(err.message || "Location required for sign-in");
      return;
    }
    try {
      await api.post("/clock/in", { site_id: siteId, ...coords });
      toast.success("Signed in at this site");
      load();
    } catch (err) {
      logger.error("Clock in failed:", err);
      const detail = err?.response?.data?.detail || "Failed";
      const status = err?.response?.status;
      // Geofence rejection — offer to request admin override
      if (status === HTTP_FORBIDDEN && /must be within/i.test(detail)) {
        if (window.confirm(`${detail}\n\nRequest an admin override?`)) {
          try {
            await api.post("/clock/override-requests", {
              site_id: siteId,
              latitude: coords.latitude,
              longitude: coords.longitude,
              reason: window.prompt("Optional note for admin (e.g. GPS accuracy issue):") || null,
            });
            toast.success("Override request sent — admin will review");
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Could not send request");
          }
        }
      } else {
        toast.error(detail);
      }
    }
  }, [load]);

  const clockOut = useCallback(async () => {
    try {
      await api.post("/clock/out");
      toast.success("Logged out — time recorded");
      load();
    } catch (err) {
      logger.error("Clock out failed:", err);
      toast.error(err?.response?.data?.detail || "Failed");
    }
  }, [load]);

  const sitesIWorkAt = useMemo(() => {
    const ids = new Set(Object.values(shifts).map((s) => s.site_id));
    return sites.filter((s) => ids.has(s.id));
  }, [sites, shifts]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-gray-900">
      {user?.must_change_password && <ChangePasswordModal />}
      {!user?.must_change_password && user?.must_accept_terms && <AcceptTermsModal />}
      <header className="border-b border-gray-300 bg-white">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <div
              className="w-9 h-9 grid place-items-center"
              style={{ background: user?.color?.bg, color: user?.color?.text, border: `1px solid ${user?.color?.border || "#ccc"}` }}>
              <span className="font-bold text-sm">{user?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2)}</span>
            </div>
            <div>
              <div className="font-black tracking-tighter text-lg leading-none">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Avidity Cleaning Services · Team Member Console</div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-button"
            className="px-3 py-1.5 border border-gray-300 text-sm flex items-center gap-1.5 hover:bg-gray-100 rounded-sm">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-px bg-gray-200 border-t border-gray-300">
          {[
            { id: "logbook", label: "Log In / Out", icon: Clock },
            { id: "schedule", label: "My Schedule", icon: Calendar },
            { id: "history", label: "Attendance Log", icon: History },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`worker-tab-${t.id}`}
              className={`flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition ${
                tab === t.id ? "bg-white text-[#002FA7]" : "bg-gray-100 text-gray-600 hover:bg-white"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="p-6 space-y-6">
        {tab === "logbook" && (
          <>
            <MySLACard />
            <LogbookTab
              open={open}
              sitesById={sitesById}
              currentShift={currentShift}
              sitesIWorkAt={sitesIWorkAt}
              sites={sites}
              onIn={clockIn}
              onOut={clockOut}
            />
          </>
        )}

        {tab === "schedule" && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-[#002FA7]" />
              <h2 className="font-black text-xl tracking-tight">My weekly schedule</h2>
            </div>
            <p className="text-sm text-gray-500 mb-3">Cells are colored by the assigned site.</p>
            <ScheduleGrid
              shifts={shifts}
              workersById={EMPTY_WORKERS}
              readOnly
              colorBySite
              sitesById={sitesById}
            />
          </section>
        )}

        {tab === "history" && (
          <HistoryTab events={history} />
        )}
      </main>
      <LegalFooter className="mt-auto" />
    </div>
  );
}

function LogbookTab({ open, sitesById, currentShift, sitesIWorkAt, sites, onIn, onOut }) {
  const scheduled = currentShift?.current_site;
  return (
    <div className="space-y-6">
      {/* Status banner */}
      <section
        className={`border ${open ? "border-green-300 bg-green-50" : "border-gray-300 bg-white"} p-5 rounded-sm`}
        data-testid="clock-panel"
      >
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-[#002FA7]" />
          <h2 className="font-black text-xl tracking-tight">Log in / Log out</h2>
        </div>

        {open ? (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px] border-l-4 border-green-600 bg-white px-4 py-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-green-800">Currently logged in</div>
              <div className="font-bold text-lg">{sitesById[open.site_id]?.name || "Unknown site"}</div>
              <div className="text-xs text-gray-500 font-mono">
                Since {fmtDateTime(open.clock_in)}
              </div>
            </div>
            <button onClick={onOut} data-testid="clock-out-button"
              className="bg-red-600 text-white px-5 py-3 rounded-sm flex items-center gap-2 hover:bg-red-700 font-bold">
              <Square className="w-4 h-4" /> LOG OUT
            </button>
          </div>
        ) : (
          <div className="text-sm text-gray-500">You are not currently logged in to a site.</div>
        )}
      </section>

      {/* Cleaning checklist — only visible when actively logged in */}
      {open && <CleaningChecklist siteId={open.site_id} />}

      {/* Scheduled now */}
      {!open && (
        <section className="border border-gray-300 bg-white p-5 rounded-sm" data-testid="scheduled-now-card">
          <div className="flex items-center gap-2 mb-3">
            <BadgeCheck className="w-5 h-5 text-[#002FA7]" />
            <h3 className="font-black text-lg tracking-tight">Right now</h3>
          </div>
          {scheduled ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div
                className="px-4 py-2.5 border rounded-sm font-bold text-sm"
                style={{ background: scheduled.color.bg, color: scheduled.color.text, borderColor: scheduled.color.border }}
              >
                {scheduled.name}
              </div>
              <span className="text-xs text-gray-500 font-mono">
                {DAY_LABELS[currentShift.day]} · {String(currentShift.hour).padStart(2, "0")}:00
              </span>
              <button
                onClick={() => onIn(scheduled.id)}
                data-testid="quick-clock-in-button"
                className="ml-auto bg-[#002FA7] text-white px-5 py-2.5 rounded-sm flex items-center gap-2 hover:bg-[#002277] font-bold text-sm"
              >
                <Play className="w-4 h-4" /> LOG IN TO THIS SHIFT
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              No site is scheduled for you in the current hour. You can still log in to any site below if you are starting an unscheduled shift.
            </div>
          )}
        </section>
      )}

      {/* Site picker (only when not clocked in) */}
      {!open && (
        <section className="border border-gray-300 bg-white p-5 rounded-sm">
          <h3 className="font-black text-lg tracking-tight mb-1">All sites</h3>
          <p className="text-xs text-gray-500 mb-3">Pick a site to log in if your scheduled site isn&apos;t shown above.</p>
          <div className="flex flex-wrap gap-2">
            {(sitesIWorkAt.length ? sitesIWorkAt : sites).map((s) => (
              <button
                key={s.id}
                onClick={() => onIn(s.id)}
                data-testid={`clock-in-${s.id}`}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm border hover:outline hover:outline-2 hover:outline-[#002FA7] transition"
                style={{ background: s.color.bg, color: s.color.text, borderColor: s.color.border }}
              >
                <Play className="w-3.5 h-3.5" /> {s.name}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function HistoryTab({ events }) {
  return (
    <section className="border border-gray-300 bg-white rounded-sm overflow-hidden" data-testid="worker-history">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#002FA7]" />
          <h3 className="font-black text-lg tracking-tight">My attendance log</h3>
        </div>
        <span className="text-xs text-gray-500 font-mono">{events.length} record(s)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left px-4 py-2.5">Site</th>
              <th className="text-left px-4 py-2.5">Logged in</th>
              <th className="text-left px-4 py-2.5">Logged out</th>
              <th className="text-left px-4 py-2.5">Duration</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400 italic">No records yet.</td></tr>
            )}
            {events.map((e) => {
              const dur = e.clock_out
                ? Math.round((new Date(e.clock_out) - new Date(e.clock_in)) / MS_PER_MINUTE)
                : null;
              return (
                <tr key={e.id} className="border-t border-gray-100" data-testid={`history-row-${e.id}`}>
                  <td className="px-4 py-2">
                    <span
                      className="inline-block px-2.5 py-1 rounded-sm border text-xs font-bold"
                      style={e.site_color ? {
                        background: e.site_color.bg, color: e.site_color.text, borderColor: e.site_color.border,
                      } : {}}
                    >
                      {e.site_name || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtDateTime(e.clock_in)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.clock_out ? fmtDateTime(e.clock_out) : <span className="text-green-700 font-bold">— OPEN —</span>}</td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtDuration(dur)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${
                      e.clock_out ? "bg-gray-100 text-gray-700" : "bg-green-600 text-white"
                    }`}>
                      {e.clock_out ? "Closed" : "Open"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
